import { API_BASE_URL } from './config';
import { escapeHtml, isValidUrl, apiFetch, createModal, renderError, markLoaded } from './helpers';
import type { FestivalEvent, FestivalsResponse } from './types';

function renderFestivalCalendar(container: HTMLElement, festivals: FestivalEvent[]): void {
    const finnishNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
    const today = finnishNow.getFullYear() + '-' + String(finnishNow.getMonth() + 1).padStart(2, '0') + '-' + String(finnishNow.getDate()).padStart(2, '0');
    const helsinkiArea = ['helsinki', 'espoo', 'vantaa', 'kauniainen'];

    const cutoff = new Date(finnishNow);
    cutoff.setMonth(cutoff.getMonth() + 3);
    const cutoffStr = cutoff.getFullYear() + '-' + String(cutoff.getMonth() + 1).padStart(2, '0') + '-' + String(cutoff.getDate()).padStart(2, '0');
    const filtered = festivals.filter(f => f.date <= cutoffStr);

    const byMonth = new Map<string, FestivalEvent[]>();
    for (const fest of filtered) {
        const monthKey = fest.date.substring(0, 7);
        if (!byMonth.has(monthKey)) byMonth.set(monthKey, []);
        byMonth.get(monthKey)!.push(fest);
    }

    let html = '';
    for (const [monthKey, monthFestivals] of byMonth) {
        const monthDate = new Date(monthKey + '-01T00:00:00');
        const monthLabel = monthDate.toLocaleDateString('fi-FI', { month: 'long', year: 'numeric' });

        html += `<div class="festivals-month">
            <div class="festivals-month-label">${escapeHtml(monthLabel)}</div>`;

        for (const fest of monthFestivals) {
            const startDate = new Date(fest.date + 'T00:00:00');
            const startStr = startDate.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
            let dateDisplay = startStr;
            if (fest.endDate && fest.endDate !== fest.date) {
                const endDate = new Date(fest.endDate + 'T00:00:00');
                const endStr = endDate.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
                dateDisplay = `${startStr}–${endStr}`;
            }

            const isOngoing = fest.date <= today && (fest.endDate ? fest.endDate >= today : fest.date >= today);
            const isLocal = fest.location ? helsinkiArea.includes(fest.location.toLowerCase()) : false;
            const ongoingClass = isOngoing ? ' festivals-item-ongoing' : '';
            const localClass = isLocal ? ' festivals-item-local' : '';
            const ongoingBadge = isOngoing ? '<span class="festivals-ongoing-badge">Nyt</span>' : '';

            const nameHtml = fest.ticketUrl && isValidUrl(fest.ticketUrl)
                ? `<a href="${escapeHtml(fest.ticketUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(fest.name)}</a>`
                : escapeHtml(fest.name);

            const genreBadge = fest.genre
                ? `<span class="festivals-genre-badge">${escapeHtml(fest.genre)}</span>`
                : '';

            const infoIcon = fest.description
                ? `<span class="festivals-info" data-tooltip="${escapeHtml(fest.description)}">ℹ</span>`
                : '';

            const locationHtml = fest.location
                ? `<span class="festivals-location">${escapeHtml(fest.location)}</span>`
                : '';

            const priceHtml = fest.priceRange
                ? `<span class="festivals-price">${escapeHtml(fest.priceRange)}</span>`
                : '';

            html += `<div class="festivals-item${ongoingClass}${localClass}">
                <div class="festivals-item-date">${escapeHtml(dateDisplay)}${ongoingBadge}</div>
                <div class="festivals-item-details">
                    <span class="festivals-item-name">${nameHtml}${infoIcon}</span>
                    <span class="festivals-item-meta">${locationHtml}${fest.location && fest.priceRange ? ' · ' : ''}${priceHtml}</span>
                </div>
                ${genreBadge}
            </div>`;
        }

        html += '</div>';
    }

    container.innerHTML = html;
}

function openFestivalsModal(festivals: FestivalEvent[]): void {
    const modal = createModal('festivals-modal', 'festivals-modal-overlay', `
        <div class="festivals-modal">
            <div class="festivals-modal-header">
                <span class="festivals-title">Festivals — Finland</span>
                <button class="festivals-modal-close" data-modal-close aria-label="Close">&#10005;</button>
            </div>
            <div class="festivals-modal-body"></div>
        </div>`);

    const body = modal.querySelector('.festivals-modal-body') as HTMLElement;
    renderFestivalCalendar(body, festivals);
}

export async function fetchFestivals(): Promise<void> {
    const container = document.getElementById('festivals-list');
    if (!container) return;

    try {
        const response = await apiFetch(`${API_BASE_URL}/festivals`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: FestivalsResponse = await response.json();

        if (!data.festivals || data.festivals.length === 0) {
            container.innerHTML = '<p class="festivals-empty">No upcoming festivals found.</p>';
            return;
        }

        const finnishNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
        const today = finnishNow.getFullYear() + '-' + String(finnishNow.getMonth() + 1).padStart(2, '0') + '-' + String(finnishNow.getDate()).padStart(2, '0');
        const upcoming = data.festivals.filter(f => (f.endDate || f.date) >= today);
        const preview = upcoming.slice(0, 5);

        renderFestivalCalendar(container, preview);

        if (upcoming.length > 5) {
            const showAll = document.createElement('button');
            showAll.className = 'festivals-show-all';
            showAll.textContent = `Show all ${upcoming.length} festivals`;
            showAll.addEventListener('click', () => openFestivalsModal(data.festivals));
            container.appendChild(showAll);
        }

        const widget = container.closest('.festivals-widget');
        if (widget) {
            widget.classList.add('festivals-clickable');
            widget.addEventListener('click', (e: Event) => {
                const target = e.target as HTMLElement;
                if (target.closest('.festivals-show-all') || target.closest('a')) return;
                openFestivalsModal(data.festivals);
            });
        }

        markLoaded(container, 'festivals');
    } catch (error) {
        renderError(container, 'festivals', 'Could not load festival listings.', error);
    }
}
